import { defineStore } from "pinia"
import { useAuthStore } from "@/stores/auth.store"

const CURSOR_TTL_MS = 10000
const ACTIVITY_TTL_MS = 30000
const RECENT_TTL_MS = 5 * 60 * 1000

export const COLLABORATOR_STATES = {
    HOVERING: 'hovering',
    SELECTING: 'selecting',
    EDITING: 'editing',
    RECENT: 'recent'
}

const COLOR_PALETTE = [
    '#2563eb',
    '#dc2626',
    '#059669',
    '#d97706',
    '#7c3aed',
    '#0891b2',
    '#be123c',
    '#4f46e5',
    '#16a34a',
    '#c2410c'
]

function stableHash(value) {
    const input = String(value || '')
    let hash = 0
    for (let i = 0; i < input.length; i++) {
        hash = ((hash << 5) - hash) + input.charCodeAt(i)
        hash |= 0
    }
    return Math.abs(hash)
}

export function collaboratorColor(userId) {
    return COLOR_PALETTE[stableHash(userId) % COLOR_PALETTE.length]
}

function schemaKey(schemaId) {
    return String(schemaId || '')
}

function userIdFrom(data) {
    return data?.user?.id || data?.user_id
}

function targetKey(target) {
    if (!target?.resource || !target?.p_id) return ''
    return `${target.resource}:${target.p_id}`
}

function isSelf(userId) {
    const auth = useAuthStore()
    return !!auth.user?.id && String(auth.user.id) === String(userId)
}

function nowIso() {
    return new Date().toISOString()
}

export const useCollaborationPresenceStore = defineStore('collaborationPresence', {
    state: () => ({
        cursors: {},
        activities: {}
    }),
    getters: {
        cursorsForSchema: state => schemaId => Object.values(state.cursors[schemaKey(schemaId)] || {}),
        activitiesForSchema: state => schemaId => Object.values(state.activities[schemaKey(schemaId)] || {})
    },
    actions: {
        receiveCursor(data) {
            const userId = userIdFrom(data)
            if (!data?.schema_id || !userId || isSelf(userId) || !data.cursor) return
            const key = schemaKey(data.schema_id)
            this.cursors[key] = this.cursors[key] || {}
            this.cursors[key][String(userId)] = {
                user: data.user || { id: userId },
                cursor: data.cursor,
                occurred_at: data.occurred_at || nowIso(),
                seenAt: Date.now()
            }
        },
        receiveActivity(data) {
            const userId = userIdFrom(data)
            const key = schemaKey(data?.schema_id)
            const target = targetKey(data?.target)
            if (!key || !userId || isSelf(userId) || !target) return
            this.activities[key] = this.activities[key] || {}
            this.activities[key][String(userId)] = {
                user: data.user || { id: userId },
                target: data.target,
                state: data.state || COLLABORATOR_STATES.HOVERING,
                occurred_at: data.occurred_at || nowIso(),
                seenAt: Date.now()
            }
        },
        clearActivity(data) {
            const userId = userIdFrom(data)
            const key = schemaKey(data?.schema_id)
            if (!key || !userId || !this.activities[key]) return
            delete this.activities[key][String(userId)]
            if (!data?.target && this.cursors[key]) delete this.cursors[key][String(userId)]
        },
        prune(schemaId) {
            const key = schemaKey(schemaId)
            const now = Date.now()
            for (const [userId, cursor] of Object.entries(this.cursors[key] || {})) {
                if (now - cursor.seenAt > CURSOR_TTL_MS) delete this.cursors[key][userId]
            }
            for (const [userId, activity] of Object.entries(this.activities[key] || {})) {
                const ttl = activity.state === COLLABORATOR_STATES.RECENT ? RECENT_TTL_MS : ACTIVITY_TTL_MS
                if (now - activity.seenAt > ttl) delete this.activities[key][userId]
            }
        }
    }
})
